import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { WRITE, useCostWriteQueue, type Mutate, type Vars } from './useCostWriteQueue'

/**
 * Every budget write: a translated error toast on failure, then a reload of the whole view (the server computes every
 * value). Resolves to true / false so a dialog can stay open on failure; `saving` is true while any write is in flight.
 */
export function useBudgetMutations(projectId: string) {
  const { saving, run } = useCostWriteQueue()
  const [createNode] = useMutation(Mutation.CREATE_COST_NODE, WRITE)
  const [updateNode] = useMutation(Mutation.UPDATE_COST_NODE, WRITE)
  const [setEstimate] = useMutation(Mutation.SET_COST_GROUP_ESTIMATE, WRITE)
  const [moveNode] = useMutation(Mutation.MOVE_COST_NODE, WRITE)
  const [deleteNode] = useMutation(Mutation.DELETE_COST_NODE, WRITE)
  const [updateSettings] = useMutation(Mutation.UPDATE_COST_CONTROL_SETTINGS, WRITE)
  const [lockBudget] = useMutation(Mutation.LOCK_COST_BUDGET, WRITE)
  const [unlockBudget] = useMutation(Mutation.UNLOCK_COST_BUDGET, WRITE)
  const reload = [{ query: Query.COST_CONTROL, variables: { projectId } }]
  const write = (mutate: unknown, variables: Vars) => run(mutate as Mutate, variables, reload).then((r) => r.ok)

  return {
    saving,
    createNode: (input: Vars) => write(createNode, { projectId, input }),
    updateNode: (id: string, input: Vars) => write(updateNode, { id, input }),
    setGroupEstimate: (nodeId: string, amount: string | null) => write(setEstimate, { nodeId, amount }),
    moveNode: (id: string, parentId: string | null, index: number) => write(moveNode, { id, parentId, index }),
    deleteNode: (id: string) => write(deleteNode, { id }),
    updateSettings: (currency: string) => write(updateSettings, { projectId, input: { currency } }),
    updateThreshold: (value: number | null) => write(updateSettings, { projectId, input: { alertThresholdPercent: value } }),
    lock: () => write(lockBudget, { projectId }),
    unlock: () => write(unlockBudget, { projectId }),
  }
}

export type BudgetMutations = ReturnType<typeof useBudgetMutations>
